"use client";

import Translations from "@translate/components/Chat/BusinessComponents/AssetFinancialsBusinessChart";
import React, { useMemo, useState } from "react";

import { useStockDetailsMarket } from "@hooks/useStockDetailsMarket";
import { useStockFinacialsHandle } from "@hooks/useStockFinacialsHandle";
import { FINANCIALS_QUERY_PARAMS } from "@config/stock";
import { I18nUtil } from "@utils/i18n";
import type { IUseStockDetailsHandleProps } from "@/src/hooks/useStockFinacialsHandle";
import { StockDetailTable } from "@components/StockDetails/StockDetailTable";
import { ViewDetailsLink, buildStockDetailModuleHref } from "./ViewDetailsLink";

const I18n = I18nUtil.createScoped(Translations);

type SelectedTab = IUseStockDetailsHandleProps["tab"];
type SelectedSwitch = "quarterly" | "annual";

const createCache = () => {
  const store: Record<string, any> = {};
  return <T,>(key: string, value?: T): T => {
    if (value) {
      store[key] = value;
    }
    return store[key];
  };
};

interface AssetFinancialsBusinessChartProps {
  assetType: "stock" | "crypto";
  assetRouterId: string;
}

export const AssetFinancialsBusinessChart: React.FC<
  AssetFinancialsBusinessChartProps
> = ({ assetRouterId }) => {
  const [selectedTab, setSelectedTab] = useState<SelectedTab>("ic");
  const [selectedSwitch, setSelectedSwitch] =
    useState<SelectedSwitch>("annual");
  const [getCacheData] = useState(() => createCache());

  const { data: resolveData, isLoading: resolveLoading } =
    useStockDetailsMarket({
      queryParams: { ids: [assetRouterId] },
      queryKey: ["chat-financials-resolve", assetRouterId],
    });

  const stockSymbol = resolveData?.symbol || "";

  const { data: currentStockData } = useStockDetailsMarket({
    enabled: !!stockSymbol,
    symbol: stockSymbol,
    queryParams: {
      ...FINANCIALS_QUERY_PARAMS,
      "enrich.with_financials.limit": selectedSwitch === "annual" ? 5 : 20,
      "enrich.with_financials.freq":
        selectedSwitch === "annual" ? "ANNUAL" : "QUARTERLY",
    },
    queryKey: ["chat-financials", stockSymbol, selectedSwitch],
    staleTime: 30 * 60 * 1000,
  });

  const stockData = useMemo(() => {
    const hasValid = (currentStockData?.financials?.items?.length ?? 0) > 0;
    return getCacheData("stockData", hasValid ? currentStockData : undefined);
  }, [currentStockData, getCacheData]);

  const { columns, data } = useStockFinacialsHandle({
    stockData,
    tab: selectedTab,
    isPC: true,
  });

  const tabTitle = useMemo(() => {
    if (selectedTab === "ic") {
      return I18n.t("%{var0} Income", { var0: stockSymbol });
    }
    if (selectedTab === "bs") {
      return I18n.t("%{var0} Balance sheet", { var0: stockSymbol });
    }
    if (selectedTab === "cf") {
      return I18n.t("%{var0} Cash flow", { var0: stockSymbol });
    }
  }, [selectedTab, stockSymbol]);

  if (resolveLoading || !stockSymbol) {
    return (
      <div className="h-60 w-full animate-pulse bg-background_white_overlay5" />
    );
  }

  return (
    <>
      <div className="flex w-full flex-col gap-4 overflow-hidden">
        {data?.length > 0 ? (
          <>
            <div className="grid gap-y-3 md:grid-cols-[1fr_160px]">
              <div className="flex gap-1">
                <p
                  className="chips sm_chips brand_chips w-fit"
                  data-actived={selectedTab === "ic"}
                  onClick={() => setSelectedTab("ic")}
                >
                  {I18n.t("Income")}
                </p>
                <p
                  className="chips sm_chips brand_chips w-fit"
                  data-actived={selectedTab === "bs"}
                  onClick={() => setSelectedTab("bs")}
                >
                  {I18n.t("Balance sheet")}
                </p>
                <p
                  className="chips sm_chips brand_chips w-fit"
                  data-actived={selectedTab === "cf"}
                  onClick={() => setSelectedTab("cf")}
                >
                  {I18n.t("Cash flow")}
                </p>
              </div>
              <tab-indicator
                className="md_bill_switch bg-background_white_overlay10 md:[--capsule-bg-color:white] [--capsule-bg-color:var(--edgen-background\_brand\_overlay100)] [--capsule-border-radius:200px] [--animation-duration:0.2s] w-fit"
                effect="capsule"
              >
                <div
                  role="tab-item"
                  className="switch_group_atoms md_switch_group_atoms"
                  data-actived={selectedSwitch === "annual"}
                  onClick={() => setSelectedSwitch("annual")}
                >
                  <div className="text-center body_medium_m">
                    {I18n.t("Annual")}
                  </div>
                </div>
                <div
                  role="tab-item"
                  className="switch_group_atoms md_switch_group_atoms"
                  data-actived={selectedSwitch === "quarterly"}
                  onClick={() => setSelectedSwitch("quarterly")}
                >
                  <div className="text-center body_medium_m">
                    {I18n.t("Quarterly")}
                  </div>
                </div>
              </tab-indicator>
            </div>
            <div className="flex flex-col gap-1">
              <h2 className="headline_s text-content_primary">{tabTitle}</h2>
              <p className="body_s text-content_quaternary">
                {I18n.t(
                  "Financials in millions USD. Fiscal year is February - January.",
                )}
              </p>
            </div>
            <StockDetailTable
              data={data}
              columns={columns}
              getRowId={(row) => row.id!.toString()}
              maxHeight={500}
            />
          </>
        ) : (
          <div className="flex h-60 w-full items-center justify-center">
            <div className="h-60 w-full animate-pulse bg-background_white_overlay5" />
          </div>
        )}
      </div>
      {stockSymbol && (
        <ViewDetailsLink
          href={buildStockDetailModuleHref(stockSymbol, "financials")}
        />
      )}
    </>
  );
};
